import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabaseAdmin } from '../../../lib/supabase';
import { invoiceGenerator } from '../../../utils/invoiceGenerator';

// Supabase timestamps come WITHOUT 'Z' suffix e.g. "2026-02-17 12:24:28.912767"
// JS Date() treats that as local time, wrong by UTC offset. Append 'Z' to force UTC.
const parseSupabaseDate = (raw) => {
  if (!raw) return null;
  if (raw.includes('+') || raw.endsWith('Z')) return new Date(raw);
  return new Date(raw.replace(' ', 'T') + 'Z');
};

const formatDate = (raw) => {
  const d = parseSupabaseDate(raw);
  if (!d || isNaN(d)) return 'N/A';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

const CustomerUserProfileModal = ({ userId, onClose }) => {
  const [profile,        setProfile]        = useState(null);
  const [deckStats,      setDeckStats]      = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoading,      setIsLoading]      = useState(true);
  const [fetchError,     setFetchError]     = useState('');

  useEffect(() => { if (userId) fetchUserProfile(); }, [userId]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!userId) return;
    const channel = supabaseAdmin
      ?.channel('customer_profile_' + userId)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'deck_submissions', filter: 'user_id=eq.' + userId }, () => fetchUserProfile())
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'audit_reports',    filter: 'user_id=eq.' + userId }, () => fetchUserProfile())
      ?.subscribe();
    return () => { supabaseAdmin?.removeChannel(channel); };
  }, [userId]);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      setFetchError('');

      console.log('[Modal] fetchUserProfile userId:', userId);

      const { data: profileData, error: profileError } = await supabaseAdmin
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.maybeSingle();

      console.log('[Modal] profileData:', profileData);
      console.log('[Modal] company_name:', profileData?.company_name, '| job_title:', profileData?.job_title, '| mobile:', profileData?.mobile);

      if (profileError) { setFetchError('Profile query failed: ' + profileError.message); return; }

      if (!profileData) {
        const { data: fallbackData } = await supabaseAdmin
          ?.from('user_profiles')
          ?.select('*')
          ?.or('auth_id.eq.' + userId + ',user_id.eq.' + userId)
          ?.maybeSingle();
        if (!fallbackData) { setFetchError('No profile row found for id: ' + userId); return; }
        await loadRelatedData(fallbackData, fallbackData.id || userId);
        return;
      }

      await loadRelatedData(profileData, userId);
    } catch (error) {
      console.error('[CustomerProfileModal] Unexpected error:', error);
      setFetchError(error.message || 'Unexpected error loading profile');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRelatedData = async (profileData, lookupId) => {
    console.log('[Modal] loadRelatedData lookupId:', lookupId);

    const { data: submissions, error: submissionsError } = await supabaseAdmin
      ?.from('deck_submissions')
      ?.select('id, title, file_name, status, audit_type, submitted_at, created_at, audit_reports ( id, status, published_at )')
      ?.eq('user_id', lookupId)
      ?.order('created_at', { ascending: false });

    console.log('[Modal] submissions count:', submissions?.length, '| error:', submissionsError?.message);
    if (submissionsError) console.error('[CustomerProfileModal] submissions error:', submissionsError);

    const { data: payments, error: paymentsError } = await supabaseAdmin
      ?.from('paid_audit_orders')
      ?.select('*, deck_submissions ( id, title, status )')
      ?.eq('user_id', lookupId)
      ?.order('created_at', { ascending: false });

    console.log('[Modal] payments count:', payments?.length, '| error:', paymentsError?.message);
    if (paymentsError) console.error('[CustomerProfileModal] payments error:', paymentsError);

    const totalDecks     = submissions?.length || 0;
    const completedDecks = submissions?.filter(s => s?.audit_reports?.length > 0 && s?.audit_reports?.[0]?.published_at !== null)?.length || 0;
    const pendingDecks   = submissions?.filter(s => !s?.audit_reports?.length || s?.audit_reports?.[0]?.published_at === null)?.length || 0;
    const inReviewDecks  = submissions?.filter(s => s?.status === 'in_review')?.length || 0;

    setProfile(profileData);
    setDeckStats({ total: totalDecks, completed: completedDecks, pending: pendingDecks, inReview: inReviewDecks, submissions: submissions || [] });
    setPaymentHistory(payments || []);
  };

  // Derive subscription from actual payments, not stale DB column
  const isPaid = paymentHistory.some(p => p?.payment_status === 'captured' || p?.payment_status === 'received');

  const getStatusBadge = (status) => {
    const cfg = {
      active:   { color: 'bg-success/10 text-success',     label: 'Active',   icon: 'CheckCircle2' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive', icon: 'XCircle'      },
      pending:  { color: 'bg-warning/10 text-warning',     label: 'Pending',  icon: 'Clock'        },
    };
    const c = cfg?.[status] || cfg?.active;
    return <span className={'px-3 py-1 rounded-full text-sm font-medium ' + c?.color + ' flex items-center gap-2 w-fit'}><Icon name={c?.icon} size={14} />{c?.label}</span>;
  };

  const getSubscriptionBadge = () => isPaid
    ? <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary flex items-center gap-2 w-fit"><Icon name="Crown"   size={14} />Paid Plan</span>
    : <span className="px-3 py-1 rounded-full text-sm font-medium bg-muted text-muted-foreground flex items-center gap-2 w-fit"><Icon name="Package" size={14} />Free Plan</span>;

  const getPaymentStatusBadge = (status) => {
    const cfg = {
      captured:   { color: 'bg-success/10 text-success',     label: 'Paid',       icon: 'CheckCircle2' },
      received:   { color: 'bg-success/10 text-success',     label: 'Received',   icon: 'CheckCircle2' },
      authorized: { color: 'bg-primary/10 text-primary',     label: 'Authorized', icon: 'Clock'        },
      pending:    { color: 'bg-warning/10 text-warning',     label: 'Pending',    icon: 'Clock'        },
      failed:     { color: 'bg-error/10 text-error',         label: 'Failed',     icon: 'XCircle'      },
      refunded:   { color: 'bg-muted text-muted-foreground', label: 'Refunded',   icon: 'RotateCcw'    },
    };
    const c = cfg?.[status] || cfg?.pending;
    return <span className={'px-2.5 py-1 rounded-full text-xs font-medium ' + c?.color + ' flex items-center gap-1.5 w-fit'}><Icon name={c?.icon} size={12} />{c?.label}</span>;
  };

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '—';
    return (currency === 'INR' ? '₹' : '$') + (amount / 100)?.toFixed(2);
  };

  if (!userId) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-lg shadow-xl border border-border max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e?.stopPropagation()}>

        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="User" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Customer Profile</h2>
              <p className="text-sm text-muted-foreground">Complete registration and activity details</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
        </div>

        {isLoading ? (
          <div className="p-12 flex items-center justify-center">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading profile...</span>
          </div>

        ) : fetchError ? (
          <div className="p-12 text-center space-y-3">
            <Icon name="AlertCircle" size={48} className="mx-auto text-warning" />
            <p className="text-foreground font-medium">Could not load profile</p>
            <p className="text-sm text-muted-foreground font-mono bg-muted px-4 py-2 rounded-lg inline-block">{fetchError}</p>
            <p className="text-xs text-muted-foreground">User ID: <span className="font-mono">{userId}</span></p>
            <div className="pt-2"><Button variant="outline" size="sm" iconName="RefreshCw" onClick={fetchUserProfile}>Retry</Button></div>
          </div>

        ) : profile ? (
          <div className="p-6 space-y-6">

            {/* Basic Information */}
            <div className="bg-muted/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="UserCircle" size={20} className="text-primary" />Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Full Name</label>
                  <p className="text-foreground font-medium mt-1">{profile?.full_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Email Address</label>
                  <p className="text-foreground font-medium mt-1 flex items-center gap-2">
                    <Icon name="Mail" size={14} className="text-muted-foreground" />{profile?.email}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Company Name</label>
                  <p className="text-foreground font-medium mt-1 flex items-center gap-2">
                    <Icon name="Building2" size={14} className="text-muted-foreground" />
                    {profile?.company_name || 'Not provided'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Job Title</label>
                  <p className="text-foreground font-medium mt-1 flex items-center gap-2">
                    <Icon name="Briefcase" size={14} className="text-muted-foreground" />
                    {profile?.job_title || 'Not provided'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Mobile</label>
                  <p className="text-foreground font-medium mt-1 flex items-center gap-2">
                    <Icon name="Phone" size={14} className="text-muted-foreground" />
                    {profile?.mobile || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className="bg-muted/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Shield" size={20} className="text-primary" />Account Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Account Status</label>
                  <div className="mt-2">{getStatusBadge('active')}</div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Subscription Plan</label>
                  <div className="mt-2">{getSubscriptionBadge()}</div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Registration Date</label>
                  <p className="text-foreground font-medium mt-1 flex items-center gap-2">
                    <Icon name="Calendar" size={14} className="text-muted-foreground" />{formatDate(profile?.created_at)}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Last Updated</label>
                  <p className="text-foreground font-medium mt-1 flex items-center gap-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />{formatDate(profile?.updated_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment History */}
            {paymentHistory?.length > 0 && (
              <div className="bg-muted/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="CreditCard" size={20} className="text-primary" />Payment History
                </h3>
                <div className="space-y-3">
                  {paymentHistory?.map((payment) => (
                    <div key={payment?.id} className="bg-card rounded-lg p-4 border border-border">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Icon name="FileText" size={16} className="text-primary flex-shrink-0" />
                            <span className="font-medium text-foreground">Paid Deck Audit</span>
                            {getPaymentStatusBadge(payment?.payment_status)}
                          </div>
                          <div className="space-y-1.5 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Icon name="Calendar" size={14} />
                              <span>{formatDate(payment?.paid_at || payment?.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                              <Icon name="Hash" size={14} className="flex-shrink-0" />
                              <span className="text-xs font-mono truncate">{payment?.razorpay_payment_id || payment?.razorpay_order_id}</span>
                            </div>
                            {payment?.deck_submissions && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Icon name="Link" size={14} />
                                <span className="text-xs">Linked: {payment?.deck_submissions?.title || 'Submission ' + payment?.deck_submissions?.id?.slice(0, 8)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-xl font-bold text-foreground">{formatCurrency(payment?.amount, payment?.currency)}</p>
                            <p className="text-xs text-muted-foreground">{payment?.currency}</p>
                          </div>
                          {(payment?.payment_status === 'captured' || payment?.payment_status === 'received') && (
                            <Button variant="outline" size="sm" iconName="Download" onClick={() => invoiceGenerator?.downloadInvoice(payment, profile)}>
                              Invoice
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Total Payments:</span>
                    <span className="text-lg font-bold text-foreground">{paymentHistory?.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Total Revenue:</span>
                    <span className="text-lg font-bold text-primary">
                      {formatCurrency(
                        paymentHistory?.filter(p => p?.payment_status === 'captured' || p?.payment_status === 'received')?.reduce((sum, p) => sum + (p?.amount || 0), 0),
                        paymentHistory?.[0]?.currency || 'INR'
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Deck Statistics */}
            {deckStats && (
              <div className="bg-muted/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="BarChart3" size={20} className="text-primary" />
                  Deck Upload &amp; Analysis Statistics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { icon: 'FileText',     iconClass: 'text-primary', valueClass: '',             value: deckStats?.total,     label: 'Total Decks' },
                    { icon: 'CheckCircle2', iconClass: 'text-success', valueClass: 'text-success', value: deckStats?.completed, label: 'Completed'   },
                    { icon: 'Clock',        iconClass: 'text-warning', valueClass: 'text-warning', value: deckStats?.pending,   label: 'Pending'     },
                    { icon: 'Eye',          iconClass: 'text-primary', valueClass: 'text-primary', value: deckStats?.inReview,  label: 'In Review'   },
                  ].map(({ icon, iconClass, valueClass, value, label }) => (
                    <div key={label} className="bg-card rounded-lg p-4 border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <Icon name={icon} size={20} className={iconClass} />
                        <span className={'text-2xl font-bold ' + (valueClass || 'text-foreground')}>{value}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Submissions */}
            {deckStats?.submissions?.length > 0 && (
              <div className="bg-muted/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Activity" size={20} className="text-primary" />Recent Deck Submissions
                </h3>
                <div className="space-y-3">
                  {deckStats?.submissions?.slice(0, 5)?.map((submission, index) => {
                    const hasPublishedAudit = submission?.audit_reports?.length > 0 && submission?.audit_reports?.[0]?.published_at !== null;
                    const displayStatus     = hasPublishedAudit ? 'completed' : submission?.status;
                    return (
                      <div key={submission?.id} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary">#{index + 1}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {submission?.title || submission?.file_name || 'Submission ' + submission?.id?.slice(0, 8)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {submission?.audit_type === 'paid' ? 'Paid' : 'Free'} &bull; {formatDate(submission?.submitted_at || submission?.created_at)}
                            </p>
                          </div>
                        </div>
                        <span className={'px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ' + (
                          displayStatus === 'completed' || displayStatus === 'published' ? 'bg-success/10 text-success' :
                          displayStatus === 'in_review' ? 'bg-primary/10 text-primary' :
                          displayStatus === 'pending'   ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                        )}>
                          {displayStatus === 'published' ? 'COMPLETED' : displayStatus?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="p-12 text-center space-y-3">
            <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground font-medium">Profile not found</p>
            <p className="text-xs text-muted-foreground">User ID: <span className="font-mono">{userId}</span></p>
            <Button variant="outline" size="sm" iconName="RefreshCw" onClick={fetchUserProfile}>Retry</Button>
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>

      </div>
    </div>
  );
};

export default CustomerUserProfileModal;